import { defineStore } from 'pinia';
import {
  type AdminUserDto,
  AdminUserServiceApi,
  AppServiceApi,
  AreebAuthServiceApi,
  BranchServiceApi,
  BrandLookupServiceApi,
  CategoryLookupServiceApi,
  CognitoAuthenticationServiceApi,
  Configuration,
  DeliveryServiceApi,
  EmailServiceApi,
  OrderServiceApi,
  OrganizationServiceApi,
  ProductServiceApi,
  PromotionServiceApi,
  SourcingServiceApi,
  ScrapBatteryPriceServiceApi,
  SubBrandLookupServiceApi,
  SubCategoryLookupServiceApi,
  SupplierOrganizationServiceApi,
  SupplierServiceApi,
  UserServiceApi,
  UploadServiceApi,
  WarehouseServiceApi,
} from '@/services';
import { ErrorHandlingMiddleware } from '@/middleware';
import { useDebugStore } from '@/stores/debug.store';
import { API_URL } from '@/config';
import {
  ProductCatalogServiceApi,
  SourcingServiceApi as SupplierSourcingServiceApi,
  WarehouseServiceApi as SupplierWarehouseServiceApi,
  Configuration as SupplierConfiguration,
} from '@/services/supplier';

export interface IApiState {
  currentUser: AdminUserDto | null;
  accessToken: string;
  apiConfig: Configuration | undefined;
}

export const useApiStore = defineStore('api', {
  state: (): IApiState => ({
    currentUser: null,
    accessToken: '',
    apiConfig: new Configuration({
      basePath: API_URL,
      fetchApi: fetch,
      middleware: [new ErrorHandlingMiddleware()],
    }),
  }),
  getters: {
    AdminUserApi(): AdminUserServiceApi {
      return new AdminUserServiceApi(this.apiConfig as Configuration);
    },
    AppApi(): AppServiceApi {
      return new AppServiceApi(this.apiConfig as Configuration);
    },
    AuthApi(): AreebAuthServiceApi {
      return new AreebAuthServiceApi(this.apiConfig as Configuration);
    },
    CognitoAuthApi(): CognitoAuthenticationServiceApi {
      return new CognitoAuthenticationServiceApi(this.apiConfig as Configuration);
    },
    BranchApi(): BranchServiceApi {
      return new BranchServiceApi(this.apiConfig as Configuration);
    },
    DeliveryApi(): DeliveryServiceApi {
      return new DeliveryServiceApi(this.apiConfig as Configuration);
    },
    EmailApi(): EmailServiceApi {
      return new EmailServiceApi(this.apiConfig as Configuration);
    },
    OrderApi(): OrderServiceApi {
      return new OrderServiceApi(this.apiConfig as Configuration);
    },
    OrganizationApi(): OrganizationServiceApi {
      return new OrganizationServiceApi(this.apiConfig as Configuration);
    },
    ProductApi(): ProductServiceApi {
      return new ProductServiceApi(this.apiConfig as Configuration);
    },
    PromotionApi(): PromotionServiceApi {
      return new PromotionServiceApi(this.apiConfig as Configuration);
    },
    ScrapBatteryApi(): ScrapBatteryPriceServiceApi {
      return new ScrapBatteryPriceServiceApi(this.apiConfig as Configuration);
    },
    SourcingApi(): SourcingServiceApi {
      return new SourcingServiceApi(this.apiConfig as Configuration);
    },
    RetailerUserApi(): UserServiceApi {
      return new UserServiceApi(this.apiConfig as Configuration);
    },
    SupplierApi(): SupplierServiceApi {
      return new SupplierServiceApi(this.apiConfig as Configuration);
    },
    SupplierOrganizationApi(): SupplierOrganizationServiceApi {
      return new SupplierOrganizationServiceApi(this.apiConfig as Configuration);
    },
    CategoryLookupServiceApi(): CategoryLookupServiceApi {
      return new CategoryLookupServiceApi(this.apiConfig as Configuration);
    },
    BrandLookupServiceApi(): BrandLookupServiceApi {
      return new BrandLookupServiceApi(this.apiConfig as Configuration);
    },
    SubCategoryLookupServiceApi(): SubCategoryLookupServiceApi {
      return new SubCategoryLookupServiceApi(this.apiConfig as Configuration);
    },
    SubBrandLookupServiceApi(): SubBrandLookupServiceApi {
      return new SubBrandLookupServiceApi(this.apiConfig as Configuration);
    },
    UploadApi(): UploadServiceApi {
      return new UploadServiceApi(this.apiConfig as Configuration);
    },
    ProductCatalogApi(): ProductCatalogServiceApi {
      return new ProductCatalogServiceApi(this.apiConfig as SupplierConfiguration);
    },
    WarehouseApi(): WarehouseServiceApi {
      return new WarehouseServiceApi(this.apiConfig as Configuration);
    },
    SupplierWarehouseApi(): SupplierWarehouseServiceApi {
      return new SupplierWarehouseServiceApi(this.apiConfig as SupplierConfiguration);
    },
    SupplierSourcingApi(): SupplierSourcingServiceApi {
      return new SupplierSourcingServiceApi(this.apiConfig as SupplierConfiguration);
    },
  },
  actions: {
    getBaseUrl(): string {
      return API_URL;
    },
    getAccessToken(): string {
      return this.accessToken;
    },
    async loadCurrentUser() {
      try {
        if (!this.apiConfig) {
          throw new Error('API configuration is not initialized');
        }
        if (!this.AuthApi) {
          throw new Error('Auth API is not available');
        }
        const user = (await this.AuthApi.authenticate()) as AdminUserDto;
        if (user && user.id && user.firstName && user.lastName && user.email) {
          this.currentUser = user;
        } else {
          this.currentUser = null;
        }
        if (this.currentUser) {
          localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
        }
      } catch (e) {
        const debugStore = useDebugStore();
        debugStore.printDebugInfo('Failed to authenticate user | ', e);
        // Stale/expired token: clear local session state but DON'T push to
        // /login from here. The router guard on the next protected
        // navigation will redirect; a force-push from a service function
        // can yank users off public routes (e.g. /driver/:accessToken)
        // they're legitimately visiting in the same browser.
        this.currentUser = null;
        this.accessToken = '';
        localStorage.removeItem('currentUser');
        localStorage.removeItem('accessToken');
        this.apiConfig = new Configuration({
          basePath: API_URL,
          fetchApi: fetch,
          middleware: [new ErrorHandlingMiddleware()],
        });
      }
    },
    setCurrentUser(user: AdminUserDto) {
      this.currentUser = {
        ...this.currentUser,
        ...user,
      };
      localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
    },
    async initConfiguration() {
      this.apiConfig = new Configuration({
        basePath: API_URL,
        fetchApi: fetch,
        accessToken: this.accessToken,
        middleware: [new ErrorHandlingMiddleware()],
      });
      await this.loadCurrentUser();
    },
    async setAccessToken(token: string) {
      this.accessToken = token;
      try {
        localStorage.setItem('accessToken', token);
      } catch (e) {
        console.error('Failed to set access token in local storage', e);
      }
      await this.initConfiguration();
    },
    async initializeFromStorage() {
      const token = localStorage.getItem('accessToken');
      const userJson = localStorage.getItem('currentUser');

      if (token) {
        this.accessToken = token;
        this.apiConfig = new Configuration({
          basePath: API_URL,
          fetchApi: fetch,
          accessToken: token,
          middleware: [new ErrorHandlingMiddleware()],
        });

        if (userJson) {
          try {
            this.currentUser = JSON.parse(userJson);
          } catch (e) {
            console.error('Failed to parse user from localStorage', e);
          }
        }
        try {
          await this.loadCurrentUser();
          return true;
        } catch (e) {
          // If token validation fails, clear everything
          this.accessToken = '';
          this.currentUser = null;
          localStorage.removeItem('accessToken');
          localStorage.removeItem('currentUser');
          return false;
        }
      }

      return false;
    },
  },
});
